/**
 * Battery simulation for the solar what-if.
 *
 * Deliberately a pure function over an hour-by-hour series, kept out of the chart component: the
 * component is about drawing, and this is the part anyone will want to argue with. Every assumption
 * that could flatter the result is named below rather than buried.
 *
 * The rule the site actually operates:
 *
 *   1. Solar always has first claim on the battery. Surplus solar - generation above what the site
 *      is using in that hour - charges it before anything else.
 *   2. The battery discharges to cover whatever the site cannot meet from solar, so grid import is
 *      the last resort.
 *   3. Only if night top-up is enabled, and only during the night band, does it buy from the grid to
 *      fill whatever solar left empty - up to a ceiling if one is set.
 *
 * That ordering matters and is not arbitrary. A captured solar kWh is worth its full avoided import
 * because the solar cost nothing; a night-charged kWh only earns the day/night spread minus losses,
 * which at Lee Strand is about 3.4c against roughly 25c. Charging from the grid into space that
 * tomorrow's solar would have filled therefore destroys value. The ceiling exists for exactly that.
 */

/** One interval of measured site behaviour. Grid is SIGNED: negative is export. */
export interface BatteryHour {
  ts: Date;
  solarKwh: number;
  gridKwh: number;
  /**
   * What one kWp would generate in this hour, from the EU's PVGIS model for the site. Only on a site
   * with no solar meter, where the client types the kWp they are thinking of (see addedKwp).
   */
  kwpKwh?: number;
}

export interface BatteryInputs {
  hours: BatteryHour[];
  /** Solar multiplier from the slider. 1 = as metered. */
  multiplier: number;
  /** kWp of estimated solar on a site with none metered. See BatteryHour.kwpKwh. */
  addedKwp?: number;
  /** Inverter AC limit in kW. Generation above it is clipped, not delivered. Null = no limit known. */
  inverterKw: number | null;

  capacityKwh: number;
  chargeKw: number;
  dischargeKw: number;
  /** Percentage, e.g. 90. */
  roundTripPct: number;

  allowGridCharge: boolean;
  /** Percentage ceiling for the night top-up. Null means charge to full. */
  nightChargeToPct: number | null;

  /**
   * Dispatch on the clock instead of on price: fill from the grid every night, then discharge into
   * whatever the site is using during the day, without asking whether the trade pays.
   *
   * This is Donal's ask from 18/09/26, and his framing is the reason it exists: "it's more usage
   * than anything else". He wants to know how many UNITS move from the day to the night, and he
   * will price it himself afterwards because the rates differ per client. The priced path cannot
   * answer that, because without a night rate on file it refuses to charge at all and without a day
   * rate it refuses to discharge, so a site with no tariffs shows a battery doing nothing.
   *
   * The simplifying assumption is his too: the battery fills every night. No forecast, no attempt to
   * hold out for the expensive hours.
   */
  dispatchOnClock?: boolean;

  /** Marginal cent per kWh. Null day rate disables every money figure rather than guessing one. */
  dayCent: number | null;
  weekendDayCent: number | null;
  nightCent: number | null;
  /** What an exported unit earns. Null or 0 means export is worth nothing to this site. */
  exportCent: number | null;

  /**
   * The rates the client entered in the what-if, each with its own hours. When present they price
   * every hour and set the night window, and dayCent, weekendDayCent and nightCent are ignored for
   * pricing (nightCent still prices the units bought to charge, so pass the night rate's cent in it).
   * Absent or empty means the site has none saved and the back-end rates above apply.
   */
  rates?: TariffBand[] | null;

  /**
   * Record what moved in and out of the battery in each interval, for the chart's benefit. Off by
   * default: the sweep runs this function once per size on every slider move, and only one of those
   * runs is ever drawn.
   */
  trace?: boolean;
}

export interface BatteryResult {
  capacityKwh: number;

  /** Surplus solar that would have been exported and was stored instead. */
  solarCapturedKwh: number;
  /** Export left over after the battery took what it could. */
  exportRemainingKwh: number;
  /** Export there would have been with no battery at all. */
  exportWithoutBatteryKwh: number;

  /** Bought on the night band purely to resell into the day. Zero unless night top-up is on. */
  nightChargedKwh: number;

  /** Delivered out of the battery. Less than what went in, by the round-trip loss. */
  dischargedKwh: number;
  /** Import the site did not have to buy, valued at the band it would have been bought in. */
  importAvoidedKwh: number;

  /** Euro over the window simulated, not annualised. Null when rates are missing. */
  savingEur: number | null;
  /** Export revenue given up by storing units instead of selling them. Zero where export earns nothing. */
  exportRevenueLostEur: number | null;
  /** savingEur minus the night-charging bill and any lost export revenue. */
  netEur: number | null;

  /** Extrapolated from netEur across the window's real length. Null when netEur is null. */
  netEurPerYear: number | null;

  /**
   * solarCapturedKwh scaled to a year, on the same basis as netEurPerYear.
   *
   * The table used to show energy captured over the SELECTED WINDOW beside a euro figure PER YEAR,
   * so the two columns sat on different time bases: a two-month window read "19,877 caught" next to
   * "13,406 a year". Dividing one by the other gave a rate that means nothing. Both are annual now.
   */
  solarCapturedKwhPerYear: number | null;

  /**
   * The same two flows a year, in units, because units are the answer Donal asked for on 18/09/26.
   *
   * nightChargedKwhPerYear is what gets BOUGHT on the night band and would not have been bought at
   * all without a battery. dischargedKwhPerYear is what comes back OUT, and it is the smaller of the
   * two by the round-trip loss. Showing both is the point: the gap between them is the loss, in
   * units, with no rate needed to see it.
   */
  nightChargedKwhPerYear: number | null;
  dischargedKwhPerYear: number | null;
  costEur: number | null;
  /** Years. Null without a cost, and null rather than Infinity when the battery never pays back. */
  paybackYears: number | null;

  /** Full-capacity equivalents over the window. High numbers shorten a battery's life. */
  fullCycles: number;

  /**
   * What moved in and out of the battery, interval by interval, or null unless `trace` was asked for.
   *
   * Three separate flows, because they land on opposite sides of the chart's axis and mean different
   * things to a reader:
   *
   *   capturedKwh     surplus solar stored instead of exported. Part of what would have been the
   *                   export band, so it is drawn inside it.
   *   gridChargedKwh  bought from the grid on the night band to resell into the day. This is extra
   *                   import that would not otherwise have happened, so it ADDS to the bar.
   *   dischargedKwh   delivered back out to the site. This is import that did not have to be bought,
   *                   so it is carved OUT of the import already on the bar.
   *
   * Keeping them apart is the whole point: netting them into one number would draw a battery that
   * charges and discharges in the same hour as having done nothing, which is the opposite of true.
   */
  flowPerHour: { ts: Date; capturedKwh: number; gridChargedKwh: number; dischargedKwh: number }[] | null;
}

/**
 * One rate with the hours it applies, local time. ToHour is exclusive, and a FromHour later than
 * ToHour runs past midnight, so night is 23 to 8. The day rate has no hours: it is the price for
 * every hour no other rate covers.
 */
export interface TariffBand {
  kind: 'day' | 'night' | 'evening' | 'other';
  cent: number;
  fromHour: number | null;
  toHour: number | null;
}

/**
 * The night window when the client has not set one: 23:00 up to 08:00.
 *
 * Jayden, 02/10/26: night starts at about 23:00, which is also Donal's sizing guide's figure. It used
 * to be 00:00 for charging (Donal on the 24/09/26 call, "may vary by contract") and 23:00 for the rate,
 * two windows for one thing. Now there is one, and a client's own night rate overrides it, because
 * the hours belong to their contract rather than to this code.
 *
 * ESB's night boundaries shift by an hour between summer and winter time. This uses the winter
 * definition throughout, which slightly understates the night window in summer and so understates
 * night charging rather than flattering it.
 */
export const DEFAULT_NIGHT_FROM_HOUR = 23;
export const DEFAULT_NIGHT_TO_HOUR = 8;

/** Whether an hour of the day falls in [from, to), wrapping past midnight when from is later. */
/**
 * Hours from this one to the end of the band, counting this one: 9 at 23:00 in a 23:00 to 08:00
 * band, 1 at 07:00. At least 1, so it can always be divided by.
 */
export function hoursLeftInBand(hour: number, from: number, to: number): number {
  if (!hourInBand(hour, from, to)) return 1;
  const left = from < to ? to - hour : (hour >= from ? 24 - hour + to : to - hour);
  return Math.max(1, left);
}

export function hourInBand(hour: number, from: number, to: number): boolean {
  return from < to ? hour >= from && hour < to : hour >= from || hour < to;
}

/** The night window: the client's night rate's hours, else the default. */
export function nightHours(rates?: TariffBand[] | null): { from: number; to: number } {
  const night = rates?.find(r => r.kind === 'night' && r.fromHour != null && r.toHour != null);
  return night
    ? { from: night.fromHour as number, to: night.toHour as number }
    : { from: DEFAULT_NIGHT_FROM_HOUR, to: DEFAULT_NIGHT_TO_HOUR };
}

export function isNightHour(ts: Date, rates?: TariffBand[] | null): boolean {
  const n = nightHours(rates);
  return hourInBand(ts.getHours(), n.from, n.to);
}

function isWeekend(ts: Date): boolean {
  const d = ts.getDay();
  return d === 0 || d === 6;
}

/**
 * Marginal cent per kWh for the hour.
 *
 * With the client's rates: the first rate in their list whose hours cover it, else their day rate.
 * Without: the back-end bands, night, then weekend (falling back to the weekday rate), then day.
 */
export function bandCent(ts: Date, i: Pick<BatteryInputs, 'rates' | 'dayCent' | 'weekendDayCent' | 'nightCent'>): number | null {
  if (i.rates && i.rates.length) {
    const h = ts.getHours();
    const band = i.rates.find(r => r.kind !== 'day' && r.fromHour != null && r.toHour != null
      && hourInBand(h, r.fromHour, r.toHour));
    if (band) return band.cent;
    return i.rates.find(r => r.kind === 'day')?.cent ?? null;
  }
  if (isNightHour(ts)) return i.nightCent ?? null;
  if (isWeekend(ts)) return i.weekendDayCent ?? i.dayCent ?? null;
  return i.dayCent ?? null;
}

/**
 * Walk the series once for one battery size.
 *
 * Site load is derived as solar + grid, both signed, which is only correct where the mains meter
 * covers the whole site. Where it does not, every figure here is wrong in the same direction and the
 * caller should not be offering the battery panel at all.
 */
export function simulateBattery(i: BatteryInputs): BatteryResult {
  const capacity = Math.max(0, i.capacityKwh);
  const rt = Math.min(100, Math.max(1, i.roundTripPct)) / 100;
  // Split the round trip evenly across both legs, so charging and discharging are each ~5% lossy at
  // 90% rather than one side absorbing the whole loss.
  const legEff = Math.sqrt(rt);

  const nightCeiling = i.nightChargeToPct == null
    ? capacity
    : capacity * Math.min(100, Math.max(0, i.nightChargeToPct)) / 100;

  let soc = 0;

  /**
   * Cent paid for whatever is currently stored, so the battery can refuse to discharge at a loss.
   *
   * Without this the simulation happily buys at the night rate at 3am and gives it straight back at
   * the night rate at 5am, losing the round trip every time - the site runs around the clock, so
   * there is always a deficit to discharge into. Real controllers hold until the expensive band, and
   * the honest way to model that is to compare what a delivered kWh cost against what it displaces.
   */
  let socCostCent = 0;

  let solarCaptured = 0;
  let exportRemaining = 0;
  let exportWithout = 0;
  let nightCharged = 0;
  let discharged = 0;
  let importAvoided = 0;

  let savingCent = 0;
  let nightCostCent = 0;
  let exportLostCent = 0;
  let ratesComplete = true;

  let energyIn = 0;

  const trace: BatteryResult['flowPerHour'] = i.trace ? [] : null;

  // One night window for the rate, the clock and the charging, read once rather than every hour.
  const night = nightHours(i.rates);
  const inNight = (ts: Date) => hourInBand(ts.getHours(), night.from, night.to);

  for (const h of i.hours) {
    // Generation after the slider, clipped by the inverter. An hour of kWh at a kW limit is the same
    // number, so the cap applies directly.
    let gen = h.solarKwh * i.multiplier + (i.addedKwp ?? 0) * (h.kwpKwh ?? 0);
    if (i.inverterKw != null && i.inverterKw > 0) gen = Math.min(gen, i.inverterKw);

    const load = h.solarKwh + h.gridKwh;   // signed grid: export makes this smaller, correctly
    const surplus = Math.max(0, gen - load);
    const deficit = Math.max(0, load - gen);

    exportWithout += surplus;

    const cent = bandCent(h.ts, i);
    if (cent == null) ratesComplete = false;

    // 1. solar first
    let capturedThisHour = 0;
    let dischargedThisHour = 0;
    let gridChargedThisHour = 0;
    if (surplus > 0 && capacity > 0) {
      const room = (capacity - soc) / legEff;
      const inKwh = Math.min(surplus, i.chargeKw, room);
      if (inKwh > 0) {
        soc += inKwh * legEff;
        // Solar costs nothing to store except the export revenue given up, which is usually nil.
        socCostCent += inKwh * (i.exportCent ?? 0);
        solarCaptured += inKwh;
        energyIn += inKwh;
        exportRemaining += surplus - inKwh;
        capturedThisHour = inKwh;

        // Storing a unit forgoes whatever export would have paid for it.
        if (i.exportCent) exportLostCent += inKwh * i.exportCent;
      } else {
        exportRemaining += surplus;
      }
    } else {
      exportRemaining += surplus;
    }

    // 2. discharge to cover what solar could not - but only where it beats buying the unit now.
    //
    // Stored solar costs nothing, so it discharges into any hour including the night band. Stored
    // night units cost the night rate plus the round-trip loss, so they must wait for a day-band
    // hour or the trade loses money. One rule covers both, and covers a battery holding a mix.
    //
    // On the clock, none of that applies: discharge into any deficit outside the charging window.
    // "Outside the window" is what stops it handing back at 03:00 the units it just bought at 02:00,
    // which is the one piece of price logic the physical rule still needs.
    const canDischarge = i.dispatchOnClock
      ? (deficit > 0 && soc > 0 && !inNight(h.ts))
      : (deficit > 0 && soc > 0 && cent != null && cent > (socCostCent / soc) / legEff);

    if (canDischarge) {
      const avgCostCent = soc > 0 ? socCostCent / soc : 0;
      const outKwh = Math.min(deficit, i.dischargeKw, soc * legEff);
      if (outKwh > 0) {
        const drawn = outKwh / legEff;
        soc -= drawn;
        socCostCent -= avgCostCent * drawn;
        discharged += outKwh;
        importAvoided += outKwh;
        if (cent != null) savingCent += outKwh * cent;
        dischargedThisHour = outKwh;
      }
    }

    // 3. night top-up, into whatever solar left empty
    //
    // The same night window either way, the client's or 23:00 to 08:00. On the clock the fill happens
    // whether or not a night rate is on file, because the answer being asked for is in units. Priced,
    // it still refuses to buy blind: without a night rate the cost of a stored unit is unknown, and
    // every euro figure downstream would be invented.
    const inWindow = inNight(h.ts);

    if (i.allowGridCharge && inWindow && capacity > 0 && soc < nightCeiling) {
      const room = (nightCeiling - soc) / legEff;
      // Spread over what is left of the night rather than filled flat out in the first hour or two
      // (Jayden, 09/10/26: a 1,000 kWh battery drew 498 kWh at 23:00 and 345 at midnight, two spikes
      // that dwarfed the day). An even share of the room per remaining hour still ends the window
      // full, gentler on the connection, and it is how a timed charge is normally set up.
      const share = room / hoursLeftInBand(h.ts.getHours(), night.from, night.to);
      const inKwh = Math.min(i.chargeKw, share);

      if (inKwh > 0 && (i.nightCent != null || i.dispatchOnClock)) {
        soc += inKwh * legEff;
        socCostCent += inKwh * (i.nightCent ?? 0);
        nightCharged += inKwh;
        energyIn += inKwh;
        nightCostCent += inKwh * (i.nightCent ?? 0);
        gridChargedThisHour = inKwh;
        if (i.nightCent == null) ratesComplete = false;
      } else if (inKwh > 0) {
        ratesComplete = false;
      }
    }

    // Recorded once all three steps have run, so an hour that both charged and discharged reports
    // both. Every interval is recorded, zeros included: the chart needs to know an hour did nothing,
    // not merely that no entry exists for it.
    if (trace) {
      trace.push({
        ts: h.ts,
        capturedKwh: capturedThisHour,
        gridChargedKwh: gridChargedThisHour,
        dischargedKwh: dischargedThisHour
      });
    }
  }

  const savingEur = ratesComplete ? savingCent / 100 : null;
  const exportRevenueLostEur = i.exportCent ? exportLostCent / 100 : 0;
  const netEur = savingEur == null
    ? null
    : savingEur - nightCostCent / 100 - (exportRevenueLostEur ?? 0);

  // Annualise on the window's real span, not on the row count: a gap-free 240 days and a patchy 240
  // rows spread over a year are very different denominators.
  const days = windowDays(i.hours);
  const netEurPerYear = netEur == null || days <= 0 ? null : netEur * 365 / days;
  const solarCapturedKwhPerYear = days <= 0 ? null : solarCaptured * 365 / days;
  const nightChargedKwhPerYear = days <= 0 ? null : nightCharged * 365 / days;
  const dischargedKwhPerYear = days <= 0 ? null : discharged * 365 / days;

  const costEur = null;   // filled in by the caller, which knows the pricing basis

  return {
    capacityKwh: capacity,
    solarCapturedKwh: solarCaptured,
    exportRemainingKwh: exportRemaining,
    exportWithoutBatteryKwh: exportWithout,
    nightChargedKwh: nightCharged,
    dischargedKwh: discharged,
    importAvoidedKwh: importAvoided,
    savingEur,
    exportRevenueLostEur,
    netEur,
    netEurPerYear,
    solarCapturedKwhPerYear,
    nightChargedKwhPerYear,
    dischargedKwhPerYear,
    costEur,
    paybackYears: null,
    fullCycles: capacity > 0 ? energyIn / capacity : 0,
    flowPerHour: trace
  };
}

// ---- Peak shaving / MIC -------------------------------------------------------------------------

/**
 * Peak shaving is a different question from the arbitrage sweep above, so it gets its own walk.
 *
 * The sweep asks "what is a stored kWh worth". This asks "how high does the site's demand get, and
 * can the battery hold it below a line". They pull in opposite directions - the cheapest hour to
 * charge in is often the hour before the peak, when the battery should be full and waiting - so
 * running one model for both would flatter one and wreck the other.
 *
 * What this buys is the connection charge. ESB sets Maximum Import Capacity off the site's highest
 * demand, and bills for that reserved capacity every year whether it is used or not. Holding the peak
 * down lets the site carry a smaller MIC, or avoid being pushed to a larger one.
 */
export interface PeakShaveInputs {
  hours: BatteryHour[];
  /** Solar multiplier from the slider. 1 = as metered. */
  multiplier: number;
  /** kWp of estimated solar on a site with none metered. See BatteryHour.kwpKwh. */
  addedKwp?: number;
  inverterKw: number | null;

  capacityKwh: number;
  chargeKw: number;
  dischargeKw: number;
  roundTripPct: number;
}

export interface PeakShaveResult {
  /** Highest grid import over the window with no battery, in kW. */
  peakBeforeKw: number;
  /** Highest grid import with the battery shaving, in kW. */
  peakAfterKw: number;
  /** What came off the peak. */
  shavedKw: number;
  /** Energy the battery had to deliver into peaks over the window. */
  dischargedKwh: number;
  /**
   * Why the battery could not shave any further. This is the question a buyer actually has, because
   * the three answers point at completely different purchases:
   *  'power'  - it hit its kW limit; a STRONGER unit shaves more, a bigger one does nothing
   *  'energy' - it emptied during a peak; MORE kWh shaves more
   *  'shape'  - it never emptied and never hit its kW limit; the site's own load leaves no room to
   *             recharge between peaks, so no battery of any size does much better
   *  'none'   - nothing to shave
   */
  limitedBy: 'power' | 'energy' | 'shape' | 'none';
}

/** Hours covered by one interval, read off the first two timestamps. Hourly data gives 1. */
function intervalHours(hours: BatteryHour[]): number {
  if (hours.length < 2) return 1;
  const h = Math.abs(hours[1].ts.getTime() - hours[0].ts.getTime()) / 3600000;
  return h > 0 ? h : 1;
}

/** Grid import per interval in kW, after the solar multiplier and the inverter cap. */
function demandKw(i: PeakShaveInputs, step: number): number[] {
  return i.hours.map(h => {
    let gen = h.solarKwh * i.multiplier + (i.addedKwp ?? 0) * (h.kwpKwh ?? 0);
    const capKwh = i.inverterKw != null && i.inverterKw > 0 ? i.inverterKw * step : null;
    if (capKwh != null) gen = Math.min(gen, capKwh);

    // Load is unchanged by the multiplier: scaling solar changes where the energy came from.
    const load = h.solarKwh + h.gridKwh;
    return Math.max(0, load - gen) / step;   // import only; export is not a demand charge
  });
}

/**
 * Walk the series holding demand below `targetKw`, and report the highest demand actually reached.
 *
 * Charging is deliberately constrained twice over: it takes surplus solar first, and any grid top-up
 * is capped so that the charging itself cannot push demand above the target. A model that charges
 * without that cap "shaves" the peak by moving it an hour earlier.
 */
function shaveToTarget(i: PeakShaveInputs, targetKw: number, step: number, demand: number[]): {
  achievedKw: number; dischargedKwh: number; minSocKwh: number;
} {
  const capacity = Math.max(0, i.capacityKwh);
  const legEff = Math.sqrt(Math.min(100, Math.max(1, i.roundTripPct)) / 100);

  let soc = capacity;          // assume commissioned full; the first peak is not a special case
  let achieved = 0;
  let discharged = 0;
  let minSoc = capacity;       // how close it came to empty - decides energy vs shape below

  for (const d of demand) {
    if (d > targetKw) {
      const needKw = Math.min(d - targetKw, i.dischargeKw);
      const availableKw = (soc * legEff) / step;
      const outKw = Math.min(needKw, availableKw);

      soc -= (outKw * step) / legEff;
      discharged += outKw * step;
      achieved = Math.max(achieved, d - outKw);
      minSoc = Math.min(minSoc, soc);
    } else {
      // Refill, but never hard enough to become the new peak.
      const roomKw = (capacity - soc) / legEff / step;
      const headroomKw = Math.max(0, targetKw - d);
      const inKw = Math.min(i.chargeKw, roomKw, headroomKw);
      soc += (inKw * step) * legEff;
      achieved = Math.max(achieved, d + inKw);
    }
  }

  return { achievedKw: achieved, dischargedKwh: discharged, minSocKwh: minSoc };
}

/**
 * Lowest peak this battery can actually hold, found by bisection on the target.
 *
 * Bisection rather than a formula because whether a target holds depends on the whole shape of the
 * year - how long each peak lasts and how much room there was to recharge between them - which no
 * closed form captures.
 */
export function shavePeak(i: PeakShaveInputs): PeakShaveResult {
  const step = intervalHours(i.hours);
  const demand = demandKw(i, step);

  const peakBefore = demand.reduce((a, b) => Math.max(a, b), 0);

  if (!i.hours.length || i.capacityKwh <= 0 || i.dischargeKw <= 0 || peakBefore <= 0) {
    return {
      peakBeforeKw: peakBefore, peakAfterKw: peakBefore, shavedKw: 0,
      dischargedKwh: 0, limitedBy: 'none'
    };
  }

  // The battery cannot shave more than its own discharge rating, whatever its capacity.
  let lo = Math.max(0, peakBefore - i.dischargeKw);
  let hi = peakBefore;
  let best = shaveToTarget(i, hi, step, demand);
  let bestTarget = hi;

  // 24 halvings takes a 500 kW range to well under 0.1 kW - far finer than the data justifies.
  for (let n = 0; n < 24; n++) {
    const mid = (lo + hi) / 2;
    const r = shaveToTarget(i, mid, step, demand);
    if (r.achievedKw <= mid + 0.001) {
      hi = mid; best = r; bestTarget = mid;      // target held, try lower
    } else {
      lo = mid;                                   // could not hold it
    }
  }

  const peakAfter = Math.min(peakBefore, Math.max(best.achievedKw, bestTarget));
  const shaved = Math.max(0, peakBefore - peakAfter);

  // Which limit bit first, in the order a buyer would care about. Shaving the full discharge rating
  // means power. Otherwise, whether it ever came close to empty separates "buy more kWh" from "the
  // site's load shape is the constraint and no battery fixes it".
  let limitedBy: PeakShaveResult['limitedBy'];
  if (shaved <= 0.001) {
    limitedBy = 'none';
  } else if (shaved >= i.dischargeKw - 0.5) {
    limitedBy = 'power';
  } else if (best.minSocKwh <= i.capacityKwh * 0.01) {
    limitedBy = 'energy';
  } else {
    limitedBy = 'shape';
  }

  return {
    peakBeforeKw: peakBefore,
    peakAfterKw: peakAfter,
    shavedKw: shaved,
    dischargedKwh: best.dischargedKwh,
    limitedBy
  };
}

/** Span of the window in days, counting the last interval rather than stopping at its start. */
export function windowDays(hours: BatteryHour[]): number {
  if (hours.length < 2) return 0;
  const first = hours[0].ts.getTime();
  const last = hours[hours.length - 1].ts.getTime();
  const step = hours.length > 1 ? (hours[1].ts.getTime() - first) : 3600000;
  return (last - first + step) / 86400000;
}

/**
 * Attach cost and payback. Kept separate from the walk because the pricing basis is a caller
 * decision: an outright quote beats a per-kWh rate when both are present.
 */
export function priceBattery(
  r: BatteryResult,
  costEur: number | null,
  costPerKwhEur: number | null
): BatteryResult {
  const cost = costEur != null && costEur > 0
    ? costEur
    : (costPerKwhEur != null && costPerKwhEur > 0 ? costPerKwhEur * r.capacityKwh : null);

  // A battery that never pays back returns null, not Infinity: "never" is the honest answer and
  // Infinity renders as a number.
  const payback = cost != null && r.netEurPerYear != null && r.netEurPerYear > 0
    ? cost / r.netEurPerYear
    : null;

  return { ...r, costEur: cost, paybackYears: payback };
}

/**
 * Sizes to try. The configured capacity is always included so the panel can answer "what about the
 * one we were quoted" alongside the ladder.
 */
export function sweepSizes(configuredKwh: number | null): number[] {
  const ladder = [50, 100, 200, 300, 500, 1000];
  const sizes = configuredKwh && configuredKwh > 0
    ? ladder.concat([configuredKwh])
    : ladder;
  return Array.from(new Set(sizes)).sort((a, b) => a - b);
}

/**
 * Power limits for a swept size. Real quotes scale power with capacity, so the configured pair is
 * read as a C-rate and applied to every size rather than pinning a 1,000 kWh battery to the 50 kWh
 * unit's inverter.
 */
export function scalePower(
  sizeKwh: number,
  configuredKwh: number | null,
  configuredKw: number | null,
  fallbackCRate: number
): number {
  if (configuredKwh && configuredKwh > 0 && configuredKw && configuredKw > 0) {
    return sizeKwh * (configuredKw / configuredKwh);
  }
  return sizeKwh * fallbackCRate;
}
